import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  LayoutGrid, History as HistoryIcon, CreditCard, Key, LogOut, Upload, Loader2, Copy, Check,
} from "lucide-react";
import { Logo, VerifyWidget, STATUS } from "@/components";
import { Seo, http, useAuth, fmtErr, logError } from "@/lib";

function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border grid-bg noise">
        <Logo />
        <div>
          <h2 className="font-display font-black text-4xl tracking-tighter leading-tight">Verify every signup.<br /><span className="text-accent">Block every fraud.</span></h2>
          <p className="text-muted-foreground mt-4 max-w-sm">Email, phone and IP risk in one API. Join teams who refuse to pay for bad data.</p>
        </div>
        <p className="text-xs text-muted-foreground font-mono">© 2026 Verix</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display font-bold text-3xl mb-6">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr("");
    try { await login(email, pw); nav("/dashboard"); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); setLoading(false); }
  };
  return (
    <><Seo title="Log in" description="Log in to your Verix account." path="/login" />
      <AuthShell title="Welcome back">
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-background border border-border px-4 py-3 outline-none focus:border-accent" data-testid="login-email" />
          <input required type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full bg-background border border-border px-4 py-3 outline-none focus:border-accent" data-testid="login-password" />
          {err && <p className="text-sm text-invalid" data-testid="login-error">{err}</p>}
          <button disabled={loading} className="btn-accent w-full py-3 flex items-center justify-center gap-2" data-testid="login-submit">{loading ? <Loader2 className="animate-spin" size={16} /> : "Log in"}</button>
        </form>
        <p className="text-sm text-muted-foreground mt-6">No account? <Link to="/signup" className="text-accent link-underline">Sign up free</Link></p>
      </AuthShell></>
  );
}

export function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(""); const [pw, setPw] = useState(""); const [name, setName] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr("");
    try { await register(email, pw, name); nav(sp.get("next") === "pricing" ? "/pricing" : "/dashboard"); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); setLoading(false); }
  };
  return (
    <><Seo title="Sign up" description="Create a free Verix account — 50 verifications a month, no card required." path="/signup" />
      <AuthShell title="Start free">
        <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
          <input placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-background border border-border px-4 py-3 outline-none focus:border-accent" data-testid="signup-name" />
          <input required type="email" placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-background border border-border px-4 py-3 outline-none focus:border-accent" data-testid="signup-email" />
          <input required type="password" placeholder="Password (min 6 chars)" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full bg-background border border-border px-4 py-3 outline-none focus:border-accent" data-testid="signup-password" />
          {err && <p className="text-sm text-invalid" data-testid="signup-error">{err}</p>}
          <button disabled={loading} className="btn-accent w-full py-3 flex items-center justify-center gap-2" data-testid="signup-submit">{loading ? <Loader2 className="animate-spin" size={16} /> : "Create free account"}</button>
        </form>
        <p className="text-xs text-muted-foreground mt-3">50 free verifications/mo · no credit card</p>
        <p className="text-sm text-muted-foreground mt-6">Have an account? <Link to="/login" className="text-accent link-underline">Log in</Link></p>
      </AuthShell></>
  );
}

export function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) { window.location.href = "/login"; return null; }
  return children;
}

const MENU = [
  { to: "/dashboard", label: "Verify", Icon: LayoutGrid, end: true },
  { to: "/dashboard/bulk", label: "Bulk", Icon: Upload },
  { to: "/dashboard/history", label: "History", Icon: HistoryIcon },
  { to: "/dashboard/billing", label: "Billing", Icon: CreditCard },
  { to: "/dashboard/api", label: "API Key", Icon: Key },
];

function DashShell({ active, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const pct = user ? Math.min(100, Math.round((user.credits_used / user.credits_limit) * 100)) : 0;
  return (
    <div className="min-h-screen grid lg:grid-cols-[240px_1fr]" data-testid="dashboard-layout">
      <aside className="border-r border-border p-5 flex flex-col">
        <Logo />
        <nav className="mt-8 flex flex-col gap-1">
          {MENU.map((m) => (
            <Link key={m.to} to={m.to} className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${active === m.to ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`menu-${m.label.toLowerCase()}`}>
              <m.Icon size={16} /> {m.label}
            </Link>
          ))}
        </nav>
        <div className="mt-auto">
          <div className="border border-border p-3 mb-3">
            <div className="flex justify-between text-xs mb-1.5"><span className="text-muted-foreground">Credits</span><span className="font-mono">{user?.credits_used}/{user?.credits_limit}</span></div>
            <div className="h-2 bg-secondary"><div className={`h-full ${pct > 90 ? "bg-invalid" : "bg-accent"}`} style={{ width: `${pct}%` }} /></div>
            <div className="text-xs text-muted-foreground mt-2 capitalize">{user?.plan} plan {user?.plan === "free" && <Link to="/dashboard/billing" className="text-accent">· Upgrade</Link>}</div>
          </div>
          <button onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="logout-btn"><LogOut size={15} /> Log out</button>
        </div>
      </aside>
      <main className="p-6 lg:p-10 max-w-4xl">{children}</main>
    </div>
  );
}

export function Dashboard() {
  const { refresh } = useAuth();
  return (
    <Protected><Seo title="Dashboard" description="Verix dashboard" path="/dashboard" />
      <DashShell active="/dashboard">
        <h1 className="font-display font-bold text-3xl mb-1">Verify</h1>
        <p className="text-muted-foreground mb-8">Run a single check with AI risk summary. Each check uses one credit.</p>
        <VerifyWidget demo={false} explain onResult={() => refresh()} />
      </DashShell>
    </Protected>
  );
}

export function BulkPage() {
  const { refresh } = useAuth();
  const [type, setType] = useState("email");
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    const values = text.split(/[\n,]+/).map((v) => v.trim()).filter(Boolean);
    if (!values.length) return;
    setLoading(true);
    try { const { data } = await http.post("/verify/bulk", { type, values, region: "US" }); setRes(data); refresh(); }
    catch (e) { alert(fmtErr(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Protected><Seo title="Bulk verify" description="Bulk verification" path="/dashboard/bulk" />
      <DashShell active="/dashboard/bulk">
        <h1 className="font-display font-bold text-3xl mb-1">Bulk verify</h1>
        <p className="text-muted-foreground mb-6">Paste up to 1,000 values (comma or newline separated). One credit each.</p>
        <div className="flex gap-2 mb-3">
          {["email", "phone", "ip"].map((t) => <button key={t} onClick={() => setType(t)} className={`px-4 py-2 text-sm capitalize ${type === t ? "bg-accent text-accent-foreground" : "btn-ghost"}`} data-testid={`bulk-type-${t}`}>{t}</button>)}
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder={"a@example.com\nb@mailinator.com"} className="w-full bg-background border border-border px-4 py-3 font-mono text-sm outline-none focus:border-accent" data-testid="bulk-input" />
        <button onClick={run} disabled={loading} className="btn-accent px-6 py-3 mt-3 flex items-center gap-2" data-testid="bulk-submit">{loading ? <Loader2 className="animate-spin" size={16} /> : "Verify all"}</button>
        {res && (
          <div className="mt-8">
            <div className="grid grid-cols-4 gap-px bg-border border border-border mb-4 text-center">
              {[["Total", res.summary.total, ""], ["Valid", res.summary.valid, "text-valid"], ["Risky", res.summary.risky, "text-risky"], ["Invalid", res.summary.invalid, "text-invalid"]].map(([l, v, c]) => (
                <div key={l} className="bg-card p-4"><div className={`font-display font-black text-2xl ${c}`}>{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>
              ))}
            </div>
            <div className="border border-border divide-y divide-border max-h-80 overflow-auto" data-testid="bulk-results">
              {res.results.map((r, i) => (
                <div key={`${r.input}-${i}`} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="font-mono text-xs truncate">{r.input}</span>
                  <span className={`text-${STATUS[r.status].cls} font-mono text-xs`}>{r.status} · {r.score}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DashShell>
    </Protected>
  );
}

export function HistoryPage() {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    const { data } = await http.get("/history");
    setItems(data.items);
  }, []);
  useEffect(() => { load(); }, [load]);
  let content;
  if (!items) content = <p className="text-muted-foreground">Loading…</p>;
  else if (items.length === 0) content = <p className="text-muted-foreground">No verifications yet.</p>;
  else content = (
          <div className="border border-border" data-testid="history-table">
            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 px-4 py-2 border-b border-border text-xs uppercase tracking-widest text-muted-foreground">
              <span>Type</span><span>Input</span><span>Score</span><span>Status</span>
            </div>
            {items.map((it) => (
              <a key={it.public_id} href={`/r/${it.public_id}`} target="_blank" rel="noreferrer" className="grid grid-cols-[auto_1fr_auto_auto] gap-4 px-4 py-3 border-b border-border hover:bg-secondary text-sm items-center">
                <span className="font-mono text-xs uppercase text-muted-foreground w-12">{it.type}</span>
                <span className="font-mono text-xs truncate">{it.input}</span>
                <span className="font-mono">{it.score}</span>
                <span className={`text-${STATUS[it.status].cls} font-mono text-xs`}>{it.status}</span>
              </a>
            ))}
          </div>
        );
  return (
    <Protected><Seo title="History" description="Verification history" path="/dashboard/history" />
      <DashShell active="/dashboard/history">
        <h1 className="font-display font-bold text-3xl mb-6">History</h1>
        {content}
      </DashShell>
    </Protected>
  );
}

export function ApiKeyPage() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  return (
    <Protected><Seo title="API Key" description="API key" path="/dashboard/api" />
      <DashShell active="/dashboard/api">
        <h1 className="font-display font-bold text-3xl mb-1">API Key</h1>
        <p className="text-muted-foreground mb-6">Use this key to call the Verix REST API from your backend.</p>
        <div className="border border-border bg-card p-4 flex items-center justify-between gap-4">
          <code className="font-mono text-sm truncate" data-testid="api-key-value">{user?.api_key}</code>
          <button onClick={() => { navigator.clipboard.writeText(user?.api_key); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="btn-ghost px-3 py-2 flex items-center gap-2 text-sm shrink-0" data-testid="api-key-copy">
            {copied ? <Check size={15} className="text-valid" /> : <Copy size={15} />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="mt-6 border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Example request</p>
          <pre className="font-mono text-xs overflow-auto text-muted-foreground">{`curl -X POST ${process.env.REACT_APP_BACKEND_URL}/api/verify \\
  -H "Authorization: Bearer ${user?.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"email","value":"name@company.com"}'`}</pre>
        </div>
      </DashShell>
    </Protected>
  );
}

export function BillingPage() {
  const { user, refresh } = useAuth();
  const [sp] = useSearchParams();
  const [msg, setMsg] = useState("");
  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid) return;
    setMsg("Confirming payment…");
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await http.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") { setMsg("✓ Payment successful — your plan is upgraded!"); refresh(); return; }
        if (data.status === "expired") { setMsg("Payment session expired."); return; }
      } catch (e) { logError("Billing status poll failed:", e); }
      if (tries++ < 6) setTimeout(poll, 2000); else setMsg("Still processing — refresh in a moment.");
    };
    poll();
  }, [sp, refresh]);
  const cancel = async () => { if (!window.confirm("Downgrade to Free plan?")) return; await http.post("/billing/cancel"); refresh(); setMsg("Plan changed to Free."); };
  return (
    <Protected><Seo title="Billing" description="Manage billing" path="/dashboard/billing" />
      <DashShell active="/dashboard/billing">
        <h1 className="font-display font-bold text-3xl mb-1">Billing</h1>
        <p className="text-muted-foreground mb-6">Manage your plan and usage.</p>
        {msg && <div className="border border-accent p-4 mb-6 font-mono text-sm" data-testid="billing-msg">{msg}</div>}
        <div className="border border-border bg-card p-6 mb-6">
          <div className="flex justify-between items-center">
            <div><p className="text-xs uppercase tracking-widest text-muted-foreground">Current plan</p><p className="font-display font-black text-3xl capitalize">{user?.plan}</p></div>
            <div className="text-right"><p className="text-xs uppercase tracking-widest text-muted-foreground">Usage this period</p><p className="font-mono text-lg">{user?.credits_used} / {user?.credits_limit}</p></div>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to="/pricing" className="btn-accent px-6 py-3" data-testid="billing-upgrade">{user?.plan === "free" ? "Upgrade plan" : "Change plan"}</Link>
          {user?.plan !== "free" && <button onClick={cancel} className="btn-ghost px-6 py-3" data-testid="billing-cancel">Cancel / downgrade</button>}
        </div>
        <p className="text-xs text-muted-foreground mt-6 font-mono">Stripe runs in TEST mode. Use card 4242 4242 4242 4242, any future expiry & CVC.</p>
      </DashShell>
    </Protected>
  );
}
